import mixpanel from 'mixpanel-browser';
import {
  LOCAL_STORAGE_AUTH_TOKEN,
  LOCAL_STORAGE_FBCLID,
  LOCAL_STORAGE_FBP,
  LOCAL_STORAGE_FB_UUID,
  LOCAL_STORAGE_USER_DATA,
  LOCAL_STORAGE_USER_FORMSTACK,
  LOCAL_STORAGE_USER_FORMSTACK_TOKEN,
  LOCAL_STORAGE_USER_ID,
} from 'common/localStorageKeys';
import { IGetSkuDataByFormatReturn, IRecurrence } from './@types';

const RECURRENCES: { [x: string | number]: IRecurrence } = {
  w: {
    recurrence: 'weekly',
  },
  1: {
    recurrence: 'monthly',
  },
  3: {
    recurrence: 'quarterly',
  },
  6: {
    recurrence: 'semester',
  },
  12: {
    recurrence: 'yearly',
  },
  lifetime: {
    recurrence: 'lifetime',
  },
};

const renderIf = (
  condition: boolean,
  component: JSX.Element,
  alternativeComponent?: JSX.Element | null,
): JSX.Element | null => (condition ? component : alternativeComponent || null);

const getUserIdFromLocalStorage = (): string | null => window.localStorage.getItem(LOCAL_STORAGE_USER_ID);
const getAuthTokenFromLocalStorage = (): string | null => window.localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN);
const getUserFromLocalStorage = (): object => JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_USER_DATA) || '{}');
const setUserLocalStorage = (data: object): void =>
  window.localStorage.setItem(LOCAL_STORAGE_USER_DATA, JSON.stringify(data));
const clearSession = (): void | null => {
  window.localStorage.clear();
  mixpanel.reset();
};

const clearLocalStore = (): void | null => {
  [LOCAL_STORAGE_AUTH_TOKEN, LOCAL_STORAGE_USER_ID, LOCAL_STORAGE_USER_FORMSTACK_TOKEN].map((item) =>
    window.localStorage.removeItem(item),
  );
};

const setIsFormstackUser = (value: boolean): void =>
  window.localStorage.setItem(LOCAL_STORAGE_USER_FORMSTACK, `${!!value}`);

const verifyIsFormstackUser = (): boolean =>
  /^true$/i.test(window.localStorage.getItem(LOCAL_STORAGE_USER_FORMSTACK) as string) || false;

const setFbclid = (fbclid: string): void => window.localStorage.setItem(LOCAL_STORAGE_FBCLID, fbclid);
const getFbclid = (): string => window.localStorage.getItem(LOCAL_STORAGE_FBCLID) as string;
const setFbp = (fbp: string): void => window.localStorage.setItem(LOCAL_STORAGE_FBP, fbp);
const getFbp = (): string => window.localStorage.getItem(LOCAL_STORAGE_FBP) as string;
const setFbUUID = (fbp: string): void => window.localStorage.setItem(LOCAL_STORAGE_FB_UUID, fbp);
const getFbUUID = (): string => window.localStorage.getItem(LOCAL_STORAGE_FB_UUID) as string;
/**
 * Obtiene o genera un valor fbp.
 * @returns {string} El valor fbp existente o generado.
 */
function getOrGenerateFbp(): string {
  // Intenta obtener la cookie _fbp
  const cookies = document.cookie.split(';');
  const fbpCookie = cookies.find((cookie) => cookie.startsWith('_fbp='));

  if (fbpCookie) {
    // Si existe, retorna el valor de la cookie _fbp
    const fbpValue = fbpCookie.split('=')[1];
    return fbpValue;
  }

  // Si no existe, genera un nuevo valor de fbp
  const version = 'fb';
  const subdomainIndex = 1; // Generalmente 1 si no se usa _fbp previamente.
  const creationTime = Date.now(); // Tiempo actual en milisegundos.
  const randomNumber = Math.floor(Math.random() * 1e10); // Número aleatorio único.

  const generatedFbp = `${version}.${subdomainIndex}.${creationTime}.${randomNumber}`;

  // Guarda la cookie _fbp en el navegador
  document.cookie = `_fbp=${generatedFbp};path=/;SameSite=Lax;`;

  return generatedFbp;
}

const isFBFlow = (): boolean => !!getFbUUID() && !!getFbclid();

const getSkuDataByFormat = (sku: string): IGetSkuDataByFormatReturn => {
  const separator = '_';
  const skuSplitted = `${sku}`.split(separator);
  const path = skuSplitted.slice(1).join(separator) || 'unknown';
  const recurrenceKey = +skuSplitted[2] || `${skuSplitted[2]}`.toLocaleLowerCase();
  const recurrence = RECURRENCES[recurrenceKey]?.recurrence || 'unknown';

  const dataByFormat = {
    skuSplitted,
    sku,
    path,
    recurrence,
  };
  return dataByFormat;
};

const getUrlPrams = (): { [x: string]: string | unknown } => {
  const params: { [x: string]: string | unknown } = {};

  if (window.location.search) {
    try {
      decodeURI(window.location.search)
        .replace('?', '')
        .split('&')
        .map((param) => param.split('='))
        .map((values) => {
          const [key, value] = values;
          params[key] = value;
          return values;
        }, {});
    } catch (error) {
      params.catchError = error;
    }
  }

  return params;
};

export {
  renderIf,
  getUserIdFromLocalStorage,
  getAuthTokenFromLocalStorage,
  clearSession,
  getUserFromLocalStorage,
  setUserLocalStorage,
  getSkuDataByFormat,
  getUrlPrams,
  verifyIsFormstackUser,
  setIsFormstackUser,
  setFbclid,
  getFbclid,
  getOrGenerateFbp,
  setFbp,
  getFbp,
  setFbUUID,
  getFbUUID,
  isFBFlow,
  clearLocalStore,
};
